import React from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import RootLayout from './layouts/RootLayout';
import RootErrorBoundary from './components/RootErrorBoundary';
import { ErrorBoundary } from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import BlogPostWrapper from './components/BlogPostWrapper';
import DocsWrapper from './components/DocsWrapper';
import LangWrapper, { LANG_PREFIXES } from './components/LangWrapper';
import LangRedirect from './components/LangRedirect';

// Lazy-load all page components
const Index = React.lazy(() => import('./pages/Index'));
const Auth = React.lazy(() => import('./pages/Auth'));
const Features = React.lazy(() => import('./pages/Features'));
const AccessControlDesign = React.lazy(() => import('./pages/AccessControlDesign'));
const IntrusionDetectionDesign = React.lazy(() => import('./pages/IntrusionDetectionDesign'));
const NetworkPlanningDesign = React.lazy(() => import('./pages/NetworkPlanningDesign'));
const SecurityComplianceDesign = React.lazy(() => import('./pages/SecurityComplianceDesign'));
const HowItWorks = React.lazy(() => import('./pages/HowItWorks'));
const UseCases = React.lazy(() => import('./pages/UseCases'));
const WarehouseCCTV = React.lazy(() => import('./pages/usecase/WarehouseCCTV'));
const RetailCCTV = React.lazy(() => import('./pages/usecase/RetailCCTV'));
const ParkingCCTV = React.lazy(() => import('./pages/usecase/ParkingCCTV'));
const SchoolCCTV = React.lazy(() => import('./pages/usecase/SchoolCCTV'));
const ConstructionCCTV = React.lazy(() => import('./pages/usecase/ConstructionCCTV'));
const IndustrialCCTV = React.lazy(() => import('./pages/usecase/IndustrialCCTV'));
const Blog = React.lazy(() => import('./pages/Blog'));
const HowToDesignCCTV = React.lazy(() => import('./pages/blog/HowToDesignCCTV'));
const FieldOfViewExplained = React.lazy(() => import('./pages/blog/FieldOfViewExplained'));
const DORIInCCTV = React.lazy(() => import('./pages/blog/DORIInCCTV'));
const CameraPlacementGuide = React.lazy(() => import('./pages/blog/CameraPlacementGuide'));
const NightVisionCCTV = React.lazy(() => import('./pages/blog/NightVisionCCTV'));
const IPvsAnalog = React.lazy(() => import('./pages/blog/IPvsAnalog'));
const FrameRateGuide = React.lazy(() => import('./pages/blog/FrameRateGuide'));
const PixelDensityExplained = React.lazy(() => import('./pages/blog/PixelDensityExplained'));
const ChoosingLens = React.lazy(() => import('./pages/blog/ChoosingLens'));
const CCTVWiring = React.lazy(() => import('./pages/blog/CCTVWiring'));
const FalseAlarmReduction = React.lazy(() => import('./pages/blog/FalseAlarmReduction'));
const CCTVMaintenance = React.lazy(() => import('./pages/blog/CCTVMaintenance'));
const HowManyCameras = React.lazy(() => import('./pages/blog/HowManyCameras'));
const BlindSpots = React.lazy(() => import('./pages/blog/BlindSpots'));
const MegapixelDistance = React.lazy(() => import('./pages/blog/MegapixelDistance'));
const WarehouseCCTVBlog = React.lazy(() => import('./pages/blog/WarehouseCCTVBlog'));
const HomeSecurityCCTV = React.lazy(() => import('./pages/blog/HomeSecurityCCTV'));
const FourKvs1080p = React.lazy(() => import('./pages/blog/FourKvs1080p'));
const PoEvsWiFi = React.lazy(() => import('./pages/blog/PoEvsWiFi'));
const CCTVInConflictZones = React.lazy(() => import('./pages/blog/CCTVInConflictZones'));
const CCTVCostEstimation = React.lazy(() => import('./pages/blog/CCTVCostEstimation'));
const BestCCTVCameras2026 = React.lazy(() => import('./pages/blog/BestCCTVCameras2026'));
const RetailCCTVGuide = React.lazy(() => import('./pages/blog/RetailCCTVGuide'));
const ParkingLotCCTV = React.lazy(() => import('./pages/blog/ParkingLotCCTV'));
const CCTVProposalGuide = React.lazy(() => import('./pages/blog/CCTVProposalGuide'));
const SchoolCCTVGuide = React.lazy(() => import('./pages/blog/SchoolCCTVGuide'));
const StorageCalculationGuide = React.lazy(() => import('./pages/blog/StorageCalculationGuide'));
const CameraTypesGuide = React.lazy(() => import('./pages/blog/CameraTypesGuide'));
const ApartmentCCTV = React.lazy(() => import('./pages/blog/ApartmentCCTV'));
const SmallBusinessCCTV = React.lazy(() => import('./pages/blog/SmallBusinessCCTV'));
const AICamerasEdgeDetection = React.lazy(() => import('./pages/blog/AICamerasEdgeDetection'));
// v1.7.4 SEO sprint — new landing pages capturing existing impression demand
const CCTVInstallationBestPractices = React.lazy(() => import('./pages/blog/CCTVInstallationBestPractices'));
const DomeCameraFieldOfViewSpecs = React.lazy(() => import('./pages/blog/DomeCameraFieldOfViewSpecs'));
const CCTVCameraTerminology = React.lazy(() => import('./pages/blog/CCTVCameraTerminology'));
const LensFOVGuide = React.lazy(() => import('./pages/lens/LensFOVGuide'));
const CameraPage = React.lazy(() => import('./pages/camera/CameraPage'));
const NVRPage = React.lazy(() => import('./pages/nvr/NVRPage'));

// Landing pages
const CCTVDesignTool = React.lazy(() => import('./pages/landing/CCTVDesignTool'));
const SecurityCameraPlanner = React.lazy(() => import('./pages/landing/SecurityCameraPlanner'));
const CameraPlacementTool = React.lazy(() => import('./pages/CameraPlacementTool'));
const CCTVLayoutSoftware = React.lazy(() => import('./pages/CCTVLayoutSoftware'));
const SecurityCameraDesignTool = React.lazy(() => import('./pages/SecurityCameraDesignTool'));
const CCTVDesignSoftware = React.lazy(() => import('./pages/CCTVDesignSoftware'));
const CCTVPlanningTool = React.lazy(() => import('./pages/CCTVPlanningTool'));
const CamerasIndex = React.lazy(() => import('./pages/CamerasIndex'));
const Resources = React.lazy(() => import('./pages/Resources'));
const Examples = React.lazy(() => import('./pages/Examples'));

// Comparison pages
const HikvisionVsDahua = React.lazy(() => import('./pages/compare/HikvisionVsDahua'));
const BrandComparisonTemplate = React.lazy(() => import('./pages/compare/BrandComparisonTemplate'));
import { BRAND_COMPARISONS } from './data/brandComparisons';
const VerticalDisciplineTemplate = React.lazy(() => import('./pages/vertical-discipline/VerticalDisciplineTemplate'));
import { VERTICAL_DISCIPLINE_ENTRIES } from './data/verticalDisciplines';
const LocationLandingTemplate = React.lazy(() => import('./pages/location/LocationLandingTemplate'));
import { EN_CITIES, PL_CITIES, EN_CITIES_EXTENDED, DE_CITIES, IT_CITIES, ES_CITIES, CS_CITIES, PT_BR_CITIES, NL_CITIES, AU_CITIES, ZA_CITIES, HU_CITIES, LT_CITIES } from './data/cityLocations';
const DomeVsBullet = React.lazy(() => import('./pages/compare/DomeVsBullet'));
const VsJVSG = React.lazy(() => import('./pages/compare/VsJVSG'));
const VsIPVideoSystem = React.lazy(() => import('./pages/compare/VsIPVideoSystem'));
const VsMilestone = React.lazy(() => import('./pages/compare/VsMilestone'));
const VsGenetec = React.lazy(() => import('./pages/compare/VsGenetec'));
const VsHikvision = React.lazy(() => import('./pages/compare/VsHikvision'));
const VsIPVM = React.lazy(() => import('./pages/compare/VsIPVM'));
const VsCCTVDesignTool = React.lazy(() => import('./pages/compare/VsCCTVDesignTool'));
const SkuComparisonPage = React.lazy(() => import('./pages/compare/SkuComparisonPage'));
const JvsgAlternative = React.lazy(() => import('./pages/JvsgAlternative'));
const CctvMapper = React.lazy(() => import('./pages/CctvMapper'));
const Testimonials = React.lazy(() => import('./pages/Testimonials'));

// Standards landing pages
const EN62676Calculator = React.lazy(() => import('./pages/standards/EN62676Calculator'));
const JohnsonCriteriaCalculator = React.lazy(() => import('./pages/standards/JohnsonCriteriaCalculator'));
const NDAACompliance = React.lazy(() => import('./pages/standards/NDAACompliance'));

// Vertical landing pages
const HospitalCCTVDesign = React.lazy(() => import('./pages/landing/HospitalCCTVDesign'));
const HotelCCTVDesign = React.lazy(() => import('./pages/landing/HotelCCTVDesign'));
const BankCCTVDesign = React.lazy(() => import('./pages/landing/BankCCTVDesign'));

// SEO blog posts targeting JVSG long-tail
const JVSGPricingExplained2026 = React.lazy(() => import('./pages/blog/JVSGPricingExplained2026'));
const SwitchingFromJVSG2026 = React.lazy(() => import('./pages/blog/SwitchingFromJVSG2026'));
const IPVMSystemDesignToolReview2026 = React.lazy(() => import('./pages/blog/IPVMSystemDesignToolReview2026'));
const CCTVDesignToolReview2026 = React.lazy(() => import('./pages/blog/CCTVDesignToolReview2026'));
const SwitchingFromIpvmOrCdt2026 = React.lazy(() => import('./pages/blog/SwitchingFromIpvmOrCdt2026'));
const BestFreeCCTVDesignSoftware2026 = React.lazy(() => import('./pages/blog/BestFreeCCTVDesignSoftware2026'));
const EN62676OODPCVSUpdate2026 = React.lazy(() => import('./pages/blog/EN62676OODPCVSUpdate2026'));
const RussianSoftwareEUComplianceReview2026 = React.lazy(() => import('./pages/blog/RussianSoftwareEUComplianceReview2026'));
const DORICalculationWalkthrough2026 = React.lazy(() => import('./pages/blog/DORICalculationWalkthrough2026'));
const BankCctvDesign2026 = React.lazy(() => import('./pages/blog/BankCctvDesign2026'));

// Day-4 sprint: lifestyle/legal long-tail blog posts (privacy, GDPR, ANPR technical, tender)
const CanMyNeighborRecord2026 = React.lazy(() => import('./pages/blog/CanMyNeighborRecord2026'));
const WorkplaceMonitoringGdpr2026 = React.lazy(() => import('./pages/blog/WorkplaceMonitoringGdpr2026'));
const LicensePlateDistanceGuide = React.lazy(() => import('./pages/blog/LicensePlateDistanceGuide'));
const HomeCctvLegalChecklist2026 = React.lazy(() => import('./pages/blog/HomeCctvLegalChecklist2026'));
const CctvSignagePictogramRequirements = React.lazy(() => import('./pages/blog/CctvSignagePictogramRequirements'));
const CctvRetentionByCountry2026 = React.lazy(() => import('./pages/blog/CctvRetentionByCountry2026'));
const CctvTenderChecklist2026 = React.lazy(() => import('./pages/blog/CctvTenderChecklist2026'));

// Vendor-bypass alternative landing pages (Day-2 sprint additions, multi-vendor positioning)
const AxisSiteDesignerAlternative = React.lazy(() => import('./pages/landing/vendor-alternative/AxisSiteDesignerAlternative'));
const HikvisionHitoolsAlternative = React.lazy(() => import('./pages/landing/vendor-alternative/HikvisionHitoolsAlternative'));
const DahuaProjectDesignToolAlternative = React.lazy(() => import('./pages/landing/vendor-alternative/DahuaProjectDesignToolAlternative'));
const FlirRavenAlternative = React.lazy(() => import('./pages/landing/vendor-alternative/FlirRavenAlternative'));
const IproSdtAlternative = React.lazy(() => import('./pages/landing/vendor-alternative/IproSdtAlternative'));
// Phase 5 — detection-sensor SEO landings (Optex catalog integration)
const PerimeterIntrusionDesign = React.lazy(() => import('./pages/landing/PerimeterIntrusionDesign'));
const OptexDesignTool = React.lazy(() => import('./pages/landing/OptexDesignTool'));
const CctvWithPirMotionDetection = React.lazy(() => import('./pages/landing/CctvWithPirMotionDetection'));

// Vertical JVSG-alternative landing pages (programmatic exact-match URLs)
const JvsgAlternativeWarehouse = React.lazy(() => import('./pages/landing/jvsg-alternative/JvsgAlternativeWarehouse'));
const JvsgAlternativeHospital = React.lazy(() => import('./pages/landing/jvsg-alternative/JvsgAlternativeHospital'));
const JvsgAlternativeSchool = React.lazy(() => import('./pages/landing/jvsg-alternative/JvsgAlternativeSchool'));
const JvsgAlternativeRetail = React.lazy(() => import('./pages/landing/jvsg-alternative/JvsgAlternativeRetail'));
const JvsgAlternativeParking = React.lazy(() => import('./pages/landing/jvsg-alternative/JvsgAlternativeParking'));
const JvsgAlternativeBank = React.lazy(() => import('./pages/landing/jvsg-alternative/JvsgAlternativeBank'));

// IPVM-alternative + CCTVDesignTool-alternative landings (v1.7.5 SEO sprint)
const IpvmAlternative = React.lazy(() => import('./pages/IpvmAlternative'));
const CCTVDesignToolAlternative = React.lazy(() => import('./pages/CCTVDesignToolAlternative'));
const IpvmAlternativeWarehouses = React.lazy(() => import('./pages/landing/ipvm-alternative/IpvmAlternativeWarehouses'));
const IpvmAlternativeRetail = React.lazy(() => import('./pages/landing/ipvm-alternative/IpvmAlternativeRetail'));
const IpvmAlternativeSchools = React.lazy(() => import('./pages/landing/ipvm-alternative/IpvmAlternativeSchools'));
const IpvmAlternativeBanks = React.lazy(() => import('./pages/landing/ipvm-alternative/IpvmAlternativeBanks'));
const IpvmAlternativeParking = React.lazy(() => import('./pages/landing/ipvm-alternative/IpvmAlternativeParking'));
const IpvmAlternativeHospitals = React.lazy(() => import('./pages/landing/ipvm-alternative/IpvmAlternativeHospitals'));
const CdtAlternativeWarehouses = React.lazy(() => import('./pages/landing/cctvdesigntool-alternative/CdtAlternativeWarehouses'));
const CdtAlternativeRetail = React.lazy(() => import('./pages/landing/cctvdesigntool-alternative/CdtAlternativeRetail'));
const CdtAlternativeSchools = React.lazy(() => import('./pages/landing/cctvdesigntool-alternative/CdtAlternativeSchools'));
const CdtAlternativeBanks = React.lazy(() => import('./pages/landing/cctvdesigntool-alternative/CdtAlternativeBanks'));
const CdtAlternativeParking = React.lazy(() => import('./pages/landing/cctvdesigntool-alternative/CdtAlternativeParking'));
const CdtAlternativeHospitals = React.lazy(() => import('./pages/landing/cctvdesigntool-alternative/CdtAlternativeHospitals'));

// Calculators
const Calculators = React.lazy(() => import('./pages/Calculators'));
const FOVCalculator = React.lazy(() => import('./pages/calculator/FOVCalculator'));
const StorageCalculator = React.lazy(() => import('./pages/calculator/StorageCalculator'));
const UPSCalculator = React.lazy(() => import('./pages/calculator/UPSCalculator'));
const PixelDensityCalculator = React.lazy(() => import('./pages/calculator/PixelDensityCalculator'));
const IFOVCalculator = React.lazy(() => import('./pages/calculator/IFOVCalculator'));
const CameraQuantityCalculator = React.lazy(() => import('./pages/calculator/CameraQuantityCalculator'));
const LensSelectorCalculator = React.lazy(() => import('./pages/calculator/LensSelectorCalculator'));
const DORICalculator = React.lazy(() => import('./pages/calculator/DORICalculator'));
const OODPCVSCalculator = React.lazy(() => import('./pages/calculator/OODPCVSCalculator'));
const BandwidthCalculator = React.lazy(() => import('./pages/calculator/BandwidthCalculator'));
const PIRSensorsCalculator = React.lazy(() => import('./pages/calculator/PIRSensorsCalculator'));
const AlarmZonesCalculator = React.lazy(() => import('./pages/calculator/AlarmZonesCalculator'));
const BeamBarrierCalculator = React.lazy(() => import('./pages/calculator/BeamBarrierCalculator'));
const SystemBudgetCalculator = React.lazy(() => import('./pages/calculator/SystemBudgetCalculator'));
const CableLengthCalculator = React.lazy(() => import('./pages/calculator/CableLengthCalculator'));
const PoECalculator = React.lazy(() => import('./pages/calculator/PoECalculator'));
const LensCalculatorLanding = React.lazy(() => import('./pages/calculator/LensCalculatorLanding'));
const StorageCalculatorLanding = React.lazy(() => import('./pages/calculator/StorageCalculatorLanding'));
const BandwidthCalculatorLanding = React.lazy(() => import('./pages/calculator/BandwidthCalculatorLanding'));

// Other pages
const Pricing = React.lazy(() => import('./pages/PricingPage'));
const RODO = React.lazy(() => import('./pages/RODO'));
const Changelog = React.lazy(() => import('./pages/Changelog'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const Glossary = React.lazy(() => import('./pages/Glossary'));
const CheckoutSuccess = React.lazy(() => import('./pages/CheckoutSuccess'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const CCTVDesignerV2 = React.lazy(() => import('./components/cctv-designer-v2/CCTVDesignerV2'));
const Portal = React.lazy(() => import('./pages/Portal'));
const ApiDocs = React.lazy(() => import('./pages/ApiDocs'));
const CameraPhysics = React.lazy(() => import('./pages/CameraPhysics'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const AboutFactsPage = React.lazy(() => import('./pages/AboutFactsPage'));
const CompliancePage = React.lazy(() => import('./pages/CompliancePage'));
const Unsubscribed = React.lazy(() => import('./pages/Unsubscribed'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

// CCTV Layout programmatic pages
const CCTVLayoutIndex = React.lazy(() => import('./pages/cctv-layout/CCTVLayoutIndex'));
const CCTVLayoutTemplate = React.lazy(() => import('./pages/cctv-layout/CCTVLayoutTemplate'));

// Docs pages
const Docs = React.lazy(() => import('./pages/Docs'));
const DocsGettingStarted = React.lazy(() => import('./pages/docs/GettingStarted'));
const DocsCreatingAProject = React.lazy(() => import('./pages/docs/CreatingAProject'));
const DocsUploadingFloorPlan = React.lazy(() => import('./pages/docs/UploadingFloorPlan'));
const DocsAddingCameras = React.lazy(() => import('./pages/docs/AddingCameras'));
const DocsFieldOfView = React.lazy(() => import('./pages/docs/FieldOfView'));
const DocsCableRouting = React.lazy(() => import('./pages/docs/CableRouting'));
const DocsPdfExport = React.lazy(() => import('./pages/docs/PdfExport'));
const DocsStorageCalculator = React.lazy(() => import('./pages/docs/StorageCalculatorDoc'));
const DocsManagingProjects = React.lazy(() => import('./pages/docs/ManagingProjects'));
const DocsSubscriptionPlans = React.lazy(() => import('./pages/docs/SubscriptionPlans'));

// Blog post slugs
const BLOG_SLUGS = [
  'how-to-design-cctv-system',
  'field-of-view-explained',
  'dori-in-cctv',
  'camera-placement-guide',
  'night-vision-cctv',
  'ip-vs-analog-cctv',
  'frame-rate-guide',
  'pixel-density-explained',
  'choosing-lens',
  'cctv-wiring',
  'false-alarm-reduction',
  'cctv-maintenance',
  'how-many-cameras',
  'blind-spots',
  'megapixel-distance',
  'warehouse-cctv',
  'home-security-cctv',
  '4k-vs-1080p-cctv',
  'poe-vs-wifi-cameras',
  'cctv-in-conflict-zones',
  'cctv-cost-estimation',
  'best-cctv-cameras-2026',
  'retail-store-cctv-layout',
  'parking-lot-cctv-layout',
  'how-to-create-cctv-proposal',
  'school-cctv-security-guide',
  'cctv-storage-calculation-guide',
  'cctv-camera-types-dome-bullet-ptz',
  'apartment-building-cctv-guide',
  'small-business-cctv-guide',
  'ai-cameras-edge-detection',
  'jvsg-pricing-explained-2026',
  'switching-from-jvsg-2026-migration-guide',
  'best-free-cctv-design-software-2026',
  'en-62676-4-oodpcvs-update-2026',
  'russian-software-eu-cctv-compliance-review-2026',
  'dori-calculation-walkthrough-2026',
  // Day-4 sprint: lifestyle/legal long-tail
  'can-my-neighbor-record-me-with-cctv-2026',
  'cctv-workplace-monitoring-gdpr-rodo-2026',
  'license-plate-camera-distance-and-focal-length-guide',
  'home-cctv-installation-legal-checklist-2026',
  'gdpr-cctv-signage-and-pictogram-requirements',
  'cctv-recording-retention-period-by-country',
  'commercial-cctv-design-tender-checklist-2026',
  // v1.7.4 SEO sprint
  'cctv-installation-best-practices',
  'dome-cctv-camera-field-of-view-specifications',
  'cctv-camera-terminology',
  // v1.7.5 SEO sprint — IPVM + CCTVDesignTool alternative supporting blogs
  'ipvm-system-design-tool-review-2026',
  'cctvdesigntool-com-review-2026',
  'switching-from-ipvm-or-cctvdesigntool-2026',
  // 2026-05-20 SEO sprint — vertical-pillar supporting blog
  'bank-cctv-design-2026',
];

// Lens focal lengths for /lens/:mm routes
const LENS_MM_VALUES = ['2.8', '3.6', '4', '6', '8', '12', '16', '25', '35', '50'];

// CCTV Layout building types
const CCTV_LAYOUT_TYPES = [
  'warehouse', 'school', 'office', 'retail-store', 'parking-lot', 'hospital',
  'hotel', 'restaurant', 'gas-station', 'bank', 'construction-site', 'apartment-building',
];

// Camera slugs — lazy-loaded to avoid pulling 40K-line cameras.ts into main bundle.
// getStaticPaths is only called during SSG build, not at CSR runtime.
const getAllCameraSlugs = () => import('@/lib/cameraSlug').then(m => m.getAllCameraSlugs());
const getAllNvrSlugs = () => import('@/lib/nvrSlug').then(m => m.getAllNvrSlugs());

// Docs slugs
const DOCS_SLUGS = [
  'getting-started',
  'creating-a-project',
  'uploading-floor-plan',
  'adding-cameras',
  'field-of-view',
  'cable-routing',
  'pdf-export',
  'storage-calculator',
  'managing-projects',
  'subscription-plans',
];

const DOCS_COMPONENTS: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  'getting-started': DocsGettingStarted,
  'creating-a-project': DocsCreatingAProject,
  'uploading-floor-plan': DocsUploadingFloorPlan,
  'adding-cameras': DocsAddingCameras,
  'field-of-view': DocsFieldOfView,
  'cable-routing': DocsCableRouting,
  'pdf-export': DocsPdfExport,
  'storage-calculator': DocsStorageCalculator,
  'managing-projects': DocsManagingProjects,
  'subscription-plans': DocsSubscriptionPlans,
};

// Map blog slug to component
const BLOG_COMPONENTS: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  'how-to-design-cctv-system': HowToDesignCCTV,
  'field-of-view-explained': FieldOfViewExplained,
  'dori-in-cctv': DORIInCCTV,
  'camera-placement-guide': CameraPlacementGuide,
  'night-vision-cctv': NightVisionCCTV,
  'ip-vs-analog-cctv': IPvsAnalog,
  'frame-rate-guide': FrameRateGuide,
  'pixel-density-explained': PixelDensityExplained,
  'choosing-lens': ChoosingLens,
  'cctv-wiring': CCTVWiring,
  'false-alarm-reduction': FalseAlarmReduction,
  'cctv-maintenance': CCTVMaintenance,
  'how-many-cameras': HowManyCameras,
  'blind-spots': BlindSpots,
  'megapixel-distance': MegapixelDistance,
  'warehouse-cctv': WarehouseCCTVBlog,
  'home-security-cctv': HomeSecurityCCTV,
  '4k-vs-1080p-cctv': FourKvs1080p,
  'poe-vs-wifi-cameras': PoEvsWiFi,
  'cctv-in-conflict-zones': CCTVInConflictZones,
  'cctv-cost-estimation': CCTVCostEstimation,
  'best-cctv-cameras-2026': BestCCTVCameras2026,
  'retail-store-cctv-layout': RetailCCTVGuide,
  'parking-lot-cctv-layout': ParkingLotCCTV,
  'how-to-create-cctv-proposal': CCTVProposalGuide,
  'school-cctv-security-guide': SchoolCCTVGuide,
  'cctv-storage-calculation-guide': StorageCalculationGuide,
  'cctv-camera-types-dome-bullet-ptz': CameraTypesGuide,
  'apartment-building-cctv-guide': ApartmentCCTV,
  'small-business-cctv-guide': SmallBusinessCCTV,
  'ai-cameras-edge-detection': AICamerasEdgeDetection,
  'jvsg-pricing-explained-2026': JVSGPricingExplained2026,
  'switching-from-jvsg-2026-migration-guide': SwitchingFromJVSG2026,
  'ipvm-system-design-tool-review-2026': IPVMSystemDesignToolReview2026,
  'cctvdesigntool-com-review-2026': CCTVDesignToolReview2026,
  'switching-from-ipvm-or-cctvdesigntool-2026': SwitchingFromIpvmOrCdt2026,
  'best-free-cctv-design-software-2026': BestFreeCCTVDesignSoftware2026,
  'en-62676-4-oodpcvs-update-2026': EN62676OODPCVSUpdate2026,
  'russian-software-eu-cctv-compliance-review-2026': RussianSoftwareEUComplianceReview2026,
  'dori-calculation-walkthrough-2026': DORICalculationWalkthrough2026,
  // Day-4 sprint: lifestyle/legal long-tail
  'can-my-neighbor-record-me-with-cctv-2026': CanMyNeighborRecord2026,
  'cctv-workplace-monitoring-gdpr-rodo-2026': WorkplaceMonitoringGdpr2026,
  'license-plate-camera-distance-and-focal-length-guide': LicensePlateDistanceGuide,
  'home-cctv-installation-legal-checklist-2026': HomeCctvLegalChecklist2026,
  'gdpr-cctv-signage-and-pictogram-requirements': CctvSignagePictogramRequirements,
  'cctv-recording-retention-period-by-country': CctvRetentionByCountry2026,
  'commercial-cctv-design-tender-checklist-2026': CctvTenderChecklist2026,
  // v1.7.4 SEO sprint
  'cctv-installation-best-practices': CCTVInstallationBestPractices,
  'dome-cctv-camera-field-of-view-specifications': DomeCameraFieldOfViewSpecs,
  'cctv-camera-terminology': CCTVCameraTerminology,
  // 2026-05-20 SEO sprint — vertical-pillar supporting blog
  'bank-cctv-design-2026': BankCctvDesign2026,
};

export const routes: RouteObject[] = [
  {
    id: 'root',
    path: '/',
    element: <RootLayout />,
    errorElement: <RootErrorBoundary />,
    children: [
      // === Public static routes ===
      { index: true, element: <Index /> },
      { path: 'auth', element: <Auth /> },
      { path: 'features', element: <Features /> },
      { path: 'access-control-design', element: <AccessControlDesign /> },
      { path: 'intrusion-detection-design', element: <IntrusionDetectionDesign /> },
      { path: 'network-planning-for-security', element: <NetworkPlanningDesign /> },
      { path: 'security-compliance', element: <SecurityComplianceDesign /> },
      { path: 'how-it-works', element: <HowItWorks /> },
      { path: 'use-cases', element: <UseCases /> },
      { path: 'use-cases/warehouse', element: <WarehouseCCTV /> },
      { path: 'use-cases/retail', element: <RetailCCTV /> },
      { path: 'use-cases/parking', element: <ParkingCCTV /> },
      { path: 'use-cases/school', element: <SchoolCCTV /> },
      { path: 'use-cases/construction', element: <ConstructionCCTV /> },
      { path: 'use-cases/industrial', element: <IndustrialCCTV /> },

      // === Blog ===
      { path: 'blog', element: <Blog /> },

      // Blog posts (English only — language via useLanguage hook)
      ...BLOG_SLUGS.map(slug => ({
        path: `blog/${slug}`,
        element: <BlogPostWrapper slug={slug} englishComponent={BLOG_COMPONENTS[slug]} />,
      })),

      // === Blog language routes (/:lang/blog/...) ===
      ...LANG_PREFIXES.flatMap(lang => [
        { path: `${lang}/blog`, element: <LangWrapper><Blog /></LangWrapper> },
        ...BLOG_SLUGS.map(slug => ({
          path: `${lang}/blog/${slug}`,
          element: <LangWrapper><BlogPostWrapper slug={slug} englishComponent={BLOG_COMPONENTS[slug]} /></LangWrapper>,
        })),
      ]),

      // === Docs ===
      { path: 'docs', element: <Docs /> },

      // Doc articles (English only — language via useLanguage hook)
      ...DOCS_SLUGS.map(slug => ({
        path: `docs/${slug}`,
        element: <DocsWrapper slug={slug} englishComponent={DOCS_COMPONENTS[slug]} />,
      })),

      // === Docs language routes (/:lang/docs/...) ===
      ...LANG_PREFIXES.flatMap(lang => [
        { path: `${lang}/docs`, element: <LangWrapper><Docs /></LangWrapper> },
        ...DOCS_SLUGS.map(slug => ({
          path: `${lang}/docs/${slug}`,
          element: <LangWrapper><DocsWrapper slug={slug} englishComponent={DOCS_COMPONENTS[slug]} /></LangWrapper>,
        })),
      ]),

      // === Lens guides (dynamic) ===
      {
        path: 'lens/:mm',
        element: <LensFOVGuide />,
        getStaticPaths: () => LENS_MM_VALUES.map(mm => `lens/${mm}`),
      },

      // === Camera pages (programmatic SEO) ===
      {
        path: 'camera/:slug',
        element: <CameraPage />,
        getStaticPaths: getAllCameraSlugs,
      },

      // === NVR / recorder pages (programmatic SEO) ===
      // Only verified+datasheetUrl entries publish — anti-hallucination.
      {
        path: 'nvr/:slug',
        element: <NVRPage />,
        getStaticPaths: getAllNvrSlugs,
      },

      // === Calculators ===
      { path: 'calculators', element: <Calculators /> },
      { path: 'calculator/fov', element: <FOVCalculator /> },
      { path: 'calculator/storage', element: <StorageCalculator /> },
      { path: 'calculator/ups', element: <UPSCalculator /> },
      { path: 'calculator/pixel-density', element: <PixelDensityCalculator /> },
      { path: 'calculator/how-many-cameras', element: <CameraQuantityCalculator /> },
      { path: 'calculator/lens-selector', element: <LensSelectorCalculator /> },
      { path: 'calculator/ifov', element: <IFOVCalculator /> },
      { path: 'calculator/dori', element: <DORICalculator /> },
      { path: 'calculator/oodpcvs', element: <OODPCVSCalculator /> },
      { path: 'calculator/bandwidth', element: <BandwidthCalculator /> },
      { path: 'calculator/pir-sensors', element: <PIRSensorsCalculator /> },
      { path: 'calculator/alarm-zones', element: <AlarmZonesCalculator /> },
      { path: 'calculator/beam-barrier', element: <BeamBarrierCalculator /> },
      { path: 'calculator/system-budget', element: <SystemBudgetCalculator /> },
      { path: 'calculator/cable-length', element: <CableLengthCalculator /> },
      { path: 'calculator/poe', element: <PoECalculator /> },
      // SEO landing pages (2026-05-24) — root-slug versions of high-volume
      // calculator queries. Distinct from /calculator/* (the canonical
      // calculator namespace) — these target the literal head terms
      // "lens calculator cctv", "cctv storage calculator", "cctv bandwidth
      // calculator" that competitors rank for with deep-link slugs.
      { path: 'lens-calculator', element: <LensCalculatorLanding /> },
      { path: 'storage-calculator', element: <StorageCalculatorLanding /> },
      { path: 'bandwidth-calculator', element: <BandwidthCalculatorLanding /> },

      // === SEO landing pages ===
      { path: 'cctv-design-tool', element: <CCTVDesignTool /> },
      { path: 'security-camera-planner', element: <SecurityCameraPlanner /> },
      { path: 'camera-placement-tool', element: <CameraPlacementTool /> },
      { path: 'cctv-layout-software', element: <CCTVLayoutSoftware /> },
      { path: 'security-camera-design-tool', element: <SecurityCameraDesignTool /> },
      { path: 'cctv-design-software', element: <CCTVDesignSoftware /> },
      { path: 'cctv-planning-tool', element: <CCTVPlanningTool /> },
      { path: 'cameras', element: <CamerasIndex /> },
      { path: 'resources', element: <Resources /> },
      { path: 'examples', element: <Examples /> },

      // === Competitor comparison pages ===
      { path: 'compare/hikvision-vs-dahua', element: <HikvisionVsDahua /> },
      // 12 brand-vs-brand comparisons from BRAND_COMPARISONS data — one
      // template component reused for all. hikvision-vs-dahua above keeps
      // its bespoke 687-line page (would be a downgrade to template-ify).
      ...BRAND_COMPARISONS.map(c => ({
        path: `compare/${c.slug}`,
        element: <BrandComparisonTemplate slug={c.slug} />,
      })),

      // === Vertical × Discipline matrix ===
      // 9 industry verticals × 4 security disciplines = 36 SEO landings
      // at flat slugs like `/hotel-access-control-design`. One template
      // renders all from data in src/data/verticalDisciplines.ts.
      ...VERTICAL_DISCIPLINE_ENTRIES.map(e => ({
        path: e.slug,
        element: <VerticalDisciplineTemplate verticalKey={e.verticalKey} disciplineKey={e.disciplineKey} />,
      })),

      // === Location SEO (EN cities — UK + US + AU + ZA) ===
      // 30 original UK+US + 5 Australia + 3 South Africa = 38 EN-locale
      // city pages under root (no language prefix — locale is en).
      ...EN_CITIES_EXTENDED.map(c => ({
        path: `cctv-design-${c.slug}`,
        element: <LocationLandingTemplate citySlug={c.slug} />,
      })),

      // === Location SEO (PL cities, /pl prefix only) ===
      // 30 Polish cities under /pl/. Wrapped in LangWrapper so the page
      // renders with PL locale active even if visitor lands without
      // hreflang trail.
      ...PL_CITIES.map(c => ({
        path: `pl/projekt-cctv-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — German cities (Sprint A) ===
      // 10 DE cities under /de/cctv-planung-{slug}. Largest underserved
      // EU paid-customer market (3 paid, 0 city pages before).
      ...DE_CITIES.map(c => ({
        path: `de/cctv-planung-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Italian cities (Sprint D) ===
      ...IT_CITIES.map(c => ({
        path: `it/progettazione-cctv-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Spanish cities (Sprint D) ===
      ...ES_CITIES.map(c => ({
        path: `es/diseno-cctv-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Czech cities (Sprint C) ===
      ...CS_CITIES.map(c => ({
        path: `cs/navrh-cctv-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Brazilian Portuguese cities (Sprint C) ===
      ...PT_BR_CITIES.map(c => ({
        path: `pt-br/projeto-cftv-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Dutch cities (Sprint D) ===
      ...NL_CITIES.map(c => ({
        path: `nl/cctv-ontwerp-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Hungarian cities (Sprint F) ===
      // 3 HU cities under /hu/cctv-tervezes-{slug}. HU = 2 Stripe paid subs,
      // 2.8% MRR, 30 GSC clicks/90d — paying market with zero competitor coverage.
      ...HU_CITIES.map(c => ({
        path: `hu/cctv-tervezes-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),

      // === Location SEO — Lithuanian cities (Sprint F) ===
      // 3 LT cities under /lt/cctv-projektavimas-{slug}. LT = 4.6% MRR
      // (top 7 globally), 2 Stripe subs, 14 GSC clicks/90d. Zero competitor coverage.
      ...LT_CITIES.map(c => ({
        path: `lt/cctv-projektavimas-${c.slug}`,
        element: <LangWrapper><LocationLandingTemplate citySlug={c.slug} /></LangWrapper>,
      })),
      { path: 'compare/dome-vs-bullet', element: <DomeVsBullet /> },
      { path: 'compare/cctvplanner-vs-jvsg', element: <VsJVSG /> },
      { path: 'compare/cctvplanner-vs-ip-video-system-design-tool', element: <VsIPVideoSystem /> },
      { path: 'compare/cctvplanner-vs-milestone-xprotect', element: <VsMilestone /> },
      { path: 'compare/cctvplanner-vs-genetec', element: <VsGenetec /> },
      { path: 'compare/cctvplanner-vs-hikvision-ivms', element: <VsHikvision /> },
      { path: 'compare/cctvplanner-vs-ipvm', element: <VsIPVM /> },
      { path: 'compare/cctvplanner-vs-cctvdesigntool', element: <VsCCTVDesignTool /> },

      // === Programmatic SKU comparison pages (Day-3 sprint, top 200 model-vs-model long-tail) ===
      // EN-only intentionally — JVSG ranks for these queries primarily in English; per-lang
      // variants would 4×-multiply SSG output for marginal SEO gain.
      {
        path: 'compare/sku/:slug',
        element: <SkuComparisonPage />,
        getStaticPaths: () => import('@/data/skuComparisonPairs.generated').then(m => m.SKU_COMPARISON_SLUGS.map(s => `compare/sku/${s}`)),
      },

      // === JVSG brand-jacking + standards + verticals (SEO push) ===
      { path: 'jvsg-alternative', element: <JvsgAlternative /> },
      { path: 'cctv-mapper', element: <CctvMapper /> },
      { path: 'en-62676-4-calculator', element: <EN62676Calculator /> },
      { path: 'johnson-criteria-calculator', element: <JohnsonCriteriaCalculator /> },
      { path: 'ndaa-cctv-compliance', element: <NDAACompliance /> },
      { path: 'hospital-cctv-design-software', element: <HospitalCCTVDesign /> },
      { path: 'hotel-cctv-design-software', element: <HotelCCTVDesign /> },
      { path: 'bank-cctv-design-software', element: <BankCCTVDesign /> },
      // Brand-jacking vanity redirects — capture URL-bar typing of "jvsg-*" guesses.
      // Not added to sitemap (would create duplicate-content risk); pure redirects.
      { path: 'jvsg-pricing', element: <Navigate to="/blog/jvsg-pricing-explained-2026" replace /> },
      { path: 'jvsg-review', element: <Navigate to="/compare/cctvplanner-vs-jvsg" replace /> },
      { path: 'jvsg-vs-cctvplanner', element: <Navigate to="/compare/cctvplanner-vs-jvsg" replace /> },
      { path: 'switch-from-jvsg', element: <Navigate to="/blog/switching-from-jvsg-2026-migration-guide" replace /> },

      // === Vendor-bypass alternative landing pages (Day-2 sprint, multi-vendor SEO) ===
      { path: 'axis-site-designer-alternative', element: <AxisSiteDesignerAlternative /> },
      { path: 'hikvision-hitools-alternative', element: <HikvisionHitoolsAlternative /> },
      { path: 'dahua-design-tool-alternative', element: <DahuaProjectDesignToolAlternative /> },
      { path: 'flir-raven-alternative', element: <FlirRavenAlternative /> },
      { path: 'i-pro-sdt-alternative', element: <IproSdtAlternative /> },

      // === Detection-sensor SEO landings (Phase 5, 2026-05-13) ===
      { path: 'perimeter-intrusion-design', element: <PerimeterIntrusionDesign /> },
      { path: 'optex-design-tool', element: <OptexDesignTool /> },
      { path: 'cctv-with-pir-motion-detection', element: <CctvWithPirMotionDetection /> },

      // === Vertical JVSG-alternative landing pages (exact-match SEO) ===
      { path: 'jvsg-alternative-for-warehouses', element: <JvsgAlternativeWarehouse /> },
      { path: 'jvsg-alternative-for-hospitals', element: <JvsgAlternativeHospital /> },
      { path: 'jvsg-alternative-for-schools', element: <JvsgAlternativeSchool /> },
      { path: 'jvsg-alternative-for-retail', element: <JvsgAlternativeRetail /> },
      { path: 'jvsg-alternative-for-parking-lots', element: <JvsgAlternativeParking /> },
      { path: 'jvsg-alternative-for-banks', element: <JvsgAlternativeBank /> },

      // === IPVM-alternative + CCTVDesignTool-alternative (v1.7.5 SEO sprint) ===
      { path: 'ipvm-alternative', element: <IpvmAlternative /> },
      { path: 'cctvdesigntool-alternative', element: <CCTVDesignToolAlternative /> },
      { path: 'ipvm-alternative-for-warehouses', element: <IpvmAlternativeWarehouses /> },
      { path: 'ipvm-alternative-for-retail', element: <IpvmAlternativeRetail /> },
      { path: 'ipvm-alternative-for-schools', element: <IpvmAlternativeSchools /> },
      { path: 'ipvm-alternative-for-banks', element: <IpvmAlternativeBanks /> },
      { path: 'ipvm-alternative-for-parking', element: <IpvmAlternativeParking /> },
      { path: 'ipvm-alternative-for-hospitals', element: <IpvmAlternativeHospitals /> },
      { path: 'cctvdesigntool-alternative-for-warehouses', element: <CdtAlternativeWarehouses /> },
      { path: 'cctvdesigntool-alternative-for-retail', element: <CdtAlternativeRetail /> },
      { path: 'cctvdesigntool-alternative-for-schools', element: <CdtAlternativeSchools /> },
      { path: 'cctvdesigntool-alternative-for-banks', element: <CdtAlternativeBanks /> },
      { path: 'cctvdesigntool-alternative-for-parking', element: <CdtAlternativeParking /> },
      { path: 'cctvdesigntool-alternative-for-hospitals', element: <CdtAlternativeHospitals /> },

      // === CCTV Layout programmatic pages ===
      { path: 'cctv-layout', element: <CCTVLayoutIndex /> },
      {
        path: 'cctv-layout/:buildingType',
        element: <CCTVLayoutTemplate />,
        getStaticPaths: () => CCTV_LAYOUT_TYPES.map(t => `cctv-layout/${t}`),
      },

      // === Other public pages ===
      { path: 'faq', element: <FAQ /> },
      { path: 'glossary', element: <Glossary /> },
      { path: 'pricing', element: <Pricing /> },
      { path: 'rodo', element: <RODO /> },
      { path: 'camera-physics', element: <CameraPhysics /> },
      { path: 'changelog', element: <LangWrapper><Changelog /></LangWrapper> },
      { path: 'unsubscribed', element: <Unsubscribed /> },

      // === Language routes for all public pages (/:lang/...) ===
      ...LANG_PREFIXES.flatMap(lang => [
        // Homepage
        { path: lang, element: <LangWrapper><Index /></LangWrapper> },

        // Static pages
        { path: `${lang}/features`, element: <LangWrapper><Features /></LangWrapper> },
        { path: `${lang}/access-control-design`, element: <LangWrapper><AccessControlDesign /></LangWrapper> },
        { path: `${lang}/intrusion-detection-design`, element: <LangWrapper><IntrusionDetectionDesign /></LangWrapper> },
        { path: `${lang}/network-planning-for-security`, element: <LangWrapper><NetworkPlanningDesign /></LangWrapper> },
        { path: `${lang}/security-compliance`, element: <LangWrapper><SecurityComplianceDesign /></LangWrapper> },
        { path: `${lang}/how-it-works`, element: <LangWrapper><HowItWorks /></LangWrapper> },
        { path: `${lang}/pricing`, element: <LangWrapper><Pricing /></LangWrapper> },
        { path: `${lang}/use-cases`, element: <LangWrapper><UseCases /></LangWrapper> },
        { path: `${lang}/use-cases/warehouse`, element: <LangWrapper><WarehouseCCTV /></LangWrapper> },
        { path: `${lang}/use-cases/retail`, element: <LangWrapper><RetailCCTV /></LangWrapper> },
        { path: `${lang}/use-cases/parking`, element: <LangWrapper><ParkingCCTV /></LangWrapper> },
        { path: `${lang}/use-cases/school`, element: <LangWrapper><SchoolCCTV /></LangWrapper> },
        { path: `${lang}/use-cases/construction`, element: <LangWrapper><ConstructionCCTV /></LangWrapper> },
        { path: `${lang}/use-cases/industrial`, element: <LangWrapper><IndustrialCCTV /></LangWrapper> },

        // Calculators
        { path: `${lang}/calculators`, element: <LangWrapper><Calculators /></LangWrapper> },
        { path: `${lang}/calculator/fov`, element: <LangWrapper><FOVCalculator /></LangWrapper> },
        { path: `${lang}/calculator/storage`, element: <LangWrapper><StorageCalculator /></LangWrapper> },
        { path: `${lang}/calculator/ups`, element: <LangWrapper><UPSCalculator /></LangWrapper> },
        { path: `${lang}/calculator/pixel-density`, element: <LangWrapper><PixelDensityCalculator /></LangWrapper> },
        { path: `${lang}/calculator/how-many-cameras`, element: <LangWrapper><CameraQuantityCalculator /></LangWrapper> },
        { path: `${lang}/calculator/lens-selector`, element: <LangWrapper><LensSelectorCalculator /></LangWrapper> },
        { path: `${lang}/calculator/ifov`, element: <LangWrapper><IFOVCalculator /></LangWrapper> },
        { path: `${lang}/calculator/dori`, element: <LangWrapper><DORICalculator /></LangWrapper> },
        { path: `${lang}/calculator/oodpcvs`, element: <LangWrapper><OODPCVSCalculator /></LangWrapper> },
        { path: `${lang}/calculator/bandwidth`, element: <LangWrapper><BandwidthCalculator /></LangWrapper> },
        { path: `${lang}/calculator/pir-sensors`, element: <LangWrapper><PIRSensorsCalculator /></LangWrapper> },
        { path: `${lang}/calculator/alarm-zones`, element: <LangWrapper><AlarmZonesCalculator /></LangWrapper> },
        { path: `${lang}/calculator/beam-barrier`, element: <LangWrapper><BeamBarrierCalculator /></LangWrapper> },
        { path: `${lang}/calculator/system-budget`, element: <LangWrapper><SystemBudgetCalculator /></LangWrapper> },
        { path: `${lang}/calculator/cable-length`, element: <LangWrapper><CableLengthCalculator /></LangWrapper> },
        { path: `${lang}/calculator/poe`, element: <LangWrapper><PoECalculator /></LangWrapper> },
        { path: `${lang}/lens-calculator`, element: <LangWrapper><LensCalculatorLanding /></LangWrapper> },
        { path: `${lang}/storage-calculator`, element: <LangWrapper><StorageCalculatorLanding /></LangWrapper> },
        { path: `${lang}/bandwidth-calculator`, element: <LangWrapper><BandwidthCalculatorLanding /></LangWrapper> },

        // SEO landing pages
        { path: `${lang}/cctv-design-tool`, element: <LangWrapper><CCTVDesignTool /></LangWrapper> },
        { path: `${lang}/security-camera-planner`, element: <LangWrapper><SecurityCameraPlanner /></LangWrapper> },
        { path: `${lang}/camera-placement-tool`, element: <LangWrapper><CameraPlacementTool /></LangWrapper> },
        { path: `${lang}/cctv-layout-software`, element: <LangWrapper><CCTVLayoutSoftware /></LangWrapper> },
        { path: `${lang}/security-camera-design-tool`, element: <LangWrapper><SecurityCameraDesignTool /></LangWrapper> },
        { path: `${lang}/cctv-design-software`, element: <LangWrapper><CCTVDesignSoftware /></LangWrapper> },
        { path: `${lang}/cctv-planning-tool`, element: <LangWrapper><CCTVPlanningTool /></LangWrapper> },

        // Compare pages
        { path: `${lang}/compare/hikvision-vs-dahua`, element: <LangWrapper><HikvisionVsDahua /></LangWrapper> },
        // Lang variants of the 12 brand-vs-brand comparisons.
        ...BRAND_COMPARISONS.map(c => ({
          path: `${lang}/compare/${c.slug}`,
          element: <LangWrapper><BrandComparisonTemplate slug={c.slug} /></LangWrapper>,
        })),
        // Lang variants of the 36 vertical × discipline matrix pages.
        ...VERTICAL_DISCIPLINE_ENTRIES.map(e => ({
          path: `${lang}/${e.slug}`,
          element: <LangWrapper><VerticalDisciplineTemplate verticalKey={e.verticalKey} disciplineKey={e.disciplineKey} /></LangWrapper>,
        })),
        { path: `${lang}/compare/dome-vs-bullet`, element: <LangWrapper><DomeVsBullet /></LangWrapper> },
        { path: `${lang}/compare/cctvplanner-vs-jvsg`, element: <LangWrapper><VsJVSG /></LangWrapper> },
        { path: `${lang}/compare/cctvplanner-vs-ip-video-system-design-tool`, element: <LangWrapper><VsIPVideoSystem /></LangWrapper> },
        { path: `${lang}/compare/cctvplanner-vs-milestone-xprotect`, element: <LangWrapper><VsMilestone /></LangWrapper> },
        { path: `${lang}/compare/cctvplanner-vs-genetec`, element: <LangWrapper><VsGenetec /></LangWrapper> },
        { path: `${lang}/compare/cctvplanner-vs-hikvision-ivms`, element: <LangWrapper><VsHikvision /></LangWrapper> },
        { path: `${lang}/compare/cctvplanner-vs-ipvm`, element: <LangWrapper><VsIPVM /></LangWrapper> },
        { path: `${lang}/compare/cctvplanner-vs-cctvdesigntool`, element: <LangWrapper><VsCCTVDesignTool /></LangWrapper> },

        // JVSG brand-jacking + standards + verticals
        { path: `${lang}/jvsg-alternative`, element: <LangWrapper><JvsgAlternative /></LangWrapper> },
        { path: `${lang}/cctv-mapper`, element: <LangWrapper><CctvMapper /></LangWrapper> },
        { path: `${lang}/en-62676-4-calculator`, element: <LangWrapper><EN62676Calculator /></LangWrapper> },
        { path: `${lang}/johnson-criteria-calculator`, element: <LangWrapper><JohnsonCriteriaCalculator /></LangWrapper> },
        { path: `${lang}/ndaa-cctv-compliance`, element: <LangWrapper><NDAACompliance /></LangWrapper> },
        { path: `${lang}/hospital-cctv-design-software`, element: <LangWrapper><HospitalCCTVDesign /></LangWrapper> },
        { path: `${lang}/hotel-cctv-design-software`, element: <LangWrapper><HotelCCTVDesign /></LangWrapper> },
        { path: `${lang}/bank-cctv-design-software`, element: <LangWrapper><BankCCTVDesign /></LangWrapper> },

        // Vendor-bypass alternative landing pages (Day-2 sprint)
        { path: `${lang}/axis-site-designer-alternative`, element: <LangWrapper><AxisSiteDesignerAlternative /></LangWrapper> },
        { path: `${lang}/hikvision-hitools-alternative`, element: <LangWrapper><HikvisionHitoolsAlternative /></LangWrapper> },
        { path: `${lang}/dahua-design-tool-alternative`, element: <LangWrapper><DahuaProjectDesignToolAlternative /></LangWrapper> },
        { path: `${lang}/flir-raven-alternative`, element: <LangWrapper><FlirRavenAlternative /></LangWrapper> },
        { path: `${lang}/i-pro-sdt-alternative`, element: <LangWrapper><IproSdtAlternative /></LangWrapper> },

        // Detection-sensor landings (Phase 5, 2026-05-13)
        { path: `${lang}/perimeter-intrusion-design`, element: <LangWrapper><PerimeterIntrusionDesign /></LangWrapper> },
        { path: `${lang}/optex-design-tool`, element: <LangWrapper><OptexDesignTool /></LangWrapper> },
        { path: `${lang}/cctv-with-pir-motion-detection`, element: <LangWrapper><CctvWithPirMotionDetection /></LangWrapper> },

        // Public testimonials page
        { path: `${lang}/testimonials`, element: <LangWrapper><Testimonials /></LangWrapper> },

        // Vertical JVSG-alternative landing pages
        { path: `${lang}/jvsg-alternative-for-warehouses`, element: <LangWrapper><JvsgAlternativeWarehouse /></LangWrapper> },
        { path: `${lang}/jvsg-alternative-for-hospitals`, element: <LangWrapper><JvsgAlternativeHospital /></LangWrapper> },
        { path: `${lang}/jvsg-alternative-for-schools`, element: <LangWrapper><JvsgAlternativeSchool /></LangWrapper> },
        { path: `${lang}/jvsg-alternative-for-retail`, element: <LangWrapper><JvsgAlternativeRetail /></LangWrapper> },
        { path: `${lang}/jvsg-alternative-for-parking-lots`, element: <LangWrapper><JvsgAlternativeParking /></LangWrapper> },
        { path: `${lang}/jvsg-alternative-for-banks`, element: <LangWrapper><JvsgAlternativeBank /></LangWrapper> },

        // IPVM-alternative + CCTVDesignTool-alternative landings (v1.7.5)
        { path: `${lang}/ipvm-alternative`, element: <LangWrapper><IpvmAlternative /></LangWrapper> },
        { path: `${lang}/cctvdesigntool-alternative`, element: <LangWrapper><CCTVDesignToolAlternative /></LangWrapper> },
        { path: `${lang}/ipvm-alternative-for-warehouses`, element: <LangWrapper><IpvmAlternativeWarehouses /></LangWrapper> },
        { path: `${lang}/ipvm-alternative-for-retail`, element: <LangWrapper><IpvmAlternativeRetail /></LangWrapper> },
        { path: `${lang}/ipvm-alternative-for-schools`, element: <LangWrapper><IpvmAlternativeSchools /></LangWrapper> },
        { path: `${lang}/ipvm-alternative-for-banks`, element: <LangWrapper><IpvmAlternativeBanks /></LangWrapper> },
        { path: `${lang}/ipvm-alternative-for-parking`, element: <LangWrapper><IpvmAlternativeParking /></LangWrapper> },
        { path: `${lang}/ipvm-alternative-for-hospitals`, element: <LangWrapper><IpvmAlternativeHospitals /></LangWrapper> },
        { path: `${lang}/cctvdesigntool-alternative-for-warehouses`, element: <LangWrapper><CdtAlternativeWarehouses /></LangWrapper> },
        { path: `${lang}/cctvdesigntool-alternative-for-retail`, element: <LangWrapper><CdtAlternativeRetail /></LangWrapper> },
        { path: `${lang}/cctvdesigntool-alternative-for-schools`, element: <LangWrapper><CdtAlternativeSchools /></LangWrapper> },
        { path: `${lang}/cctvdesigntool-alternative-for-banks`, element: <LangWrapper><CdtAlternativeBanks /></LangWrapper> },
        { path: `${lang}/cctvdesigntool-alternative-for-parking`, element: <LangWrapper><CdtAlternativeParking /></LangWrapper> },
        { path: `${lang}/cctvdesigntool-alternative-for-hospitals`, element: <LangWrapper><CdtAlternativeHospitals /></LangWrapper> },

        // CCTV Layout pages — use :buildingType param so useParams() returns
        // the type. Literal paths (previously used as a workaround for missing
        // i18n keys) made useParams undefined, the template fell back to
        // <Navigate /> and SSG rendered empty meta. The 477 cli_/clt_ keys are
        // now translated across all 19 locales (May 2026), so the param-form
        // route works without leaking raw keys to SEO.
        { path: `${lang}/cctv-layout`, element: <LangWrapper><CCTVLayoutIndex /></LangWrapper> },
        {
          path: `${lang}/cctv-layout/:buildingType`,
          element: <LangWrapper><CCTVLayoutTemplate /></LangWrapper>,
          getStaticPaths: () => CCTV_LAYOUT_TYPES.map(t => `${lang}/cctv-layout/${t}`),
        },

        // Lens guides — must use :mm param so useParams() returns mm.
        // Without it, every /<lang>/lens/<X> rendered as 4mm (the default fallback).
        {
          path: `${lang}/lens/:mm`,
          element: <LangWrapper><LensFOVGuide /></LangWrapper>,
          getStaticPaths: () => LENS_MM_VALUES.map(mm => `${lang}/lens/${mm}`),
        },

        // Other public pages
        { path: `${lang}/cameras`, element: <LangWrapper><CamerasIndex /></LangWrapper> },
        { path: `${lang}/resources`, element: <LangWrapper><Resources /></LangWrapper> },
        { path: `${lang}/examples`, element: <LangWrapper><Examples /></LangWrapper> },
        { path: `${lang}/faq`, element: <LangWrapper><FAQ /></LangWrapper> },
        { path: `${lang}/glossary`, element: <LangWrapper><Glossary /></LangWrapper> },
        { path: `${lang}/rodo`, element: <LangWrapper><RODO /></LangWrapper> },
        { path: `${lang}/camera-physics`, element: <LangWrapper><CameraPhysics /></LangWrapper> },
        { path: `${lang}/changelog`, element: <LangWrapper><Changelog /></LangWrapper> },
        { path: `${lang}/about`, element: <LangWrapper><AboutPage /></LangWrapper> },

        // Protected/app routes are unprefixed by design (lang from useLanguage,
        // not URL). If a user types /:lang/<protected>, set lang then redirect
        // to the canonical unprefixed path.
        { path: `${lang}/dashboard`, element: <LangRedirect to="/dashboard" /> },
        { path: `${lang}/cctv-design`, element: <LangRedirect to="/cctv-design" /> },
        { path: `${lang}/auth`, element: <LangRedirect to="/auth" /> },
        { path: `${lang}/api-docs`, element: <LangRedirect to="/api-docs" /> },
        { path: `${lang}/checkout-success`, element: <LangRedirect to="/checkout-success" /> },
        { path: `${lang}/demo`, element: <LangRedirect to="/demo" /> },
      ]),

      // === About page ===
      { path: 'about', element: <AboutPage /> },
      // /about/facts — atomic citation-friendly fact sheet for LLM ingest.
      // EN-only intentionally: facts are language-neutral and translating
      // them adds maintenance cost without SEO upside (no lang-specific
      // queries for these facts).
      { path: 'about/facts', element: <AboutFactsPage /> },
      // /compliance — country-by-country CCTV regulation pillar (Sprint G
      // 2026-05-21). EN-only with cross-links to native city pages.
      { path: 'compliance', element: <CompliancePage /> },

      // === Redirects for removed pages ===
      { path: 'testimonials', element: <Testimonials /> },

      // === Client Portal (public, no auth) ===
      { path: 'p/:token', element: <Portal /> },

      // === Demo project (public, read-only) ===
      {
        path: 'demo',
        element: (
          <ErrorBoundary name="demo">
            <div className="h-screen w-screen">
              <CCTVDesignerV2 demoProjectId="ef6b455c-557b-4719-8717-c0114eb9fa92" />
            </div>
          </ErrorBoundary>
        ),
      },

      // === Protected routes (excluded from SSG via includedRoutes) ===
      {
        path: 'dashboard',
        element: <ProtectedRoute><ErrorBoundary name="dashboard"><Dashboard /></ErrorBoundary></ProtectedRoute>,
      },
      {
        path: 'checkout-success',
        element: <ProtectedRoute><CheckoutSuccess /></ProtectedRoute>,
      },
      {
        path: 'cctv-design',
        element: (
          <ProtectedRoute>
            <ErrorBoundary name="designer">
              <div className="h-screen w-screen">
                <CCTVDesignerV2 />
              </div>
            </ErrorBoundary>
          </ProtectedRoute>
        ),
      },
      // === API Docs (Premium only) ===
      {
        path: 'api-docs',
        element: <ProtectedRoute><ApiDocs /></ProtectedRoute>,
      },

      // === 404 ===
      { path: '404', element: <NotFound /> },
      { path: '*', element: <NotFound /> },
    ],
  },
];
